import { Component, ChangeDetectionStrategy, inject } from "@angular/core";
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";

import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { selectQuoteId, selectSalesInfo } from "src/app/store/pages/quote/quote.selectors";
import { selectSelectedCompany } from "src/app/store/user/user.selectors";
import { DrawingActions } from "src/app/store/pages/quote/drawing/drawing.actions";

@Component({
    selector: "app-complete-drawing-dialog",
    templateUrl: "./complete-drawing-dialog.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        CoreErrorsComponent,
        ButtonModule,
        InputTextModule,
        FloatLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
    ],
})
export class CompleteDrawingDialogComponent {

    public readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly quoteId = toSignal(this.store.select(selectQuoteId));
    private readonly salesInfo = toSignal(this.store.select(selectSalesInfo));
    private readonly selectedCompany = toSignal(this.store.select(selectSelectedCompany));

    public readonly form = new FormGroup({
        sharepointUrl: new FormControl<string>("", {
            nonNullable: true,
            validators: [Validators.required, Validators.pattern(/^https?:\/\/.+/)],
        }),
    });

    public submit(): void {
        const quoteId = this.quoteId();
        const companyId = this.selectedCompany()?.id;
        const sharepointUrl = this.form.value.sharepointUrl?.trim();
        if (!quoteId || !companyId || !sharepointUrl) {
            this.ref.close();
            return;
        }

        const params = {
            quoteId,
            companyId,
            salesPerson: this.salesInfo()?.responsibleSalesPerson?.name ?? "—",
            sharepointUrl,
            drawingLink: `${window.location.origin}/${companyId}/quote/${quoteId}/drawing`,
        };
        const subject = this.translate.instant("QUOTE.DRAWING.EMAIL.COMPLETE.SUBJECT", params);
        const message = this.translate.instant("QUOTE.DRAWING.EMAIL.COMPLETE.BODY", params);

        this.store.dispatch(DrawingActions.completeDrawing({ quoteId, sharepointUrl, subject, message }));
        this.ref.close();
    }

    public closeDialog(): void {
        this.ref.close();
    }
}
